import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const Hero = ({ banners = [] }) => {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const count = banners.length;

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % count), 5500);
    return () => clearInterval(t);
  }, [count]);

  if (!count) {
    return <section className="h-[70vh] bg-pink-50 pt-20" data-testid="hero-section" />;
  }
  const banner = banners[active];

  return (
    <section className="relative w-full overflow-hidden pt-[112px]" data-testid="hero-section">
      <div className={`relative w-full ${banner.mobile_image ? "aspect-[3/4]" : "aspect-[4/3]"} sm:aspect-[16/8] lg:aspect-[21/8] bg-zinc-50 transition-[padding] duration-500`}>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 cursor-pointer"
          onClick={() => navigate(banner.cta || "/shop")}
        >
          {banner.image ? (
            <picture className="block w-full h-full">
              {banner.mobile_image && <source media="(max-width: 639px)" srcSet={banner.mobile_image} />}
              <img
                src={banner.image}
                alt={banner.title}
                className={`w-full h-full ${banner.fit === "contain" ? "object-contain bg-[#fdf2f8]" : banner.fit === "top" ? "object-cover object-top" : "object-cover object-center"}`}
                data-testid="hero-image"
              />
            </picture>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gold via-[#ff4d9e] to-[#7b0f3f]" data-testid="hero-image" />
          )}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/45 to-transparent" />        </motion.div>
      </AnimatePresence>
      </div>
      <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:bottom-10 sm:left-6 md:left-12 z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12, transition: { duration: 0.25 } }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white/85 backdrop-blur-xl border border-white/40 px-5 py-4 sm:px-7 sm:py-5 max-w-md shadow-lg shadow-pink-900/10"
            data-testid="hero-caption"
          >
            <p className="text-[9px] uppercase tracking-[0.4em] text-gold" data-testid="hero-eyebrow">{banner.brand_slug ? "Featured Brand" : "Limited Time"}</p>
            <h1 className="font-serif font-medium text-2xl sm:text-3xl md:text-4xl text-zinc-900 mt-1.5" data-testid="hero-heading">{banner.title}</h1>
            {banner.subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-1.5 sm:mt-2 leading-relaxed line-clamp-2" data-testid="hero-subcopy">{banner.subtitle}</p>}
            <button
              onClick={() => navigate(banner.cta || "/shop")}
              data-testid="hero-cta-shop"
              className="group flex items-center gap-2.5 mt-3 sm:mt-4 bg-gold text-white text-[10px] uppercase tracking-[0.3em] px-5 py-2.5 sm:px-6 sm:py-3 hover:bg-champagne transition-colors duration-300"
            >
              Shop Now
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="absolute bottom-4 right-4 sm:bottom-10 sm:right-6 md:right-12 z-10 flex gap-2.5" data-testid="hero-dots">
        {banners.map((b, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Banner ${i + 1}`}
            className={`h-1 transition-all duration-500 ${i === active ? "w-12 bg-white" : "w-6 bg-white/50 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
};
